import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, useAuth, useToast } from "@ozpanel/ui";

/**
 * What Caddy is actually doing on this node.
 *
 * Measured 2026-09-03: the caddy module exposes thirteen endpoints and the console read ONE of
 * them (/caddy/plugins). The other twelve answer precisely the questions this codebase has spent
 * the week reverse-engineering by hand:
 *
 *   /caddy/tls/audit  — WHY a certificate is held, per domain, in words. Two domains are held on
 *                       this node right now and the endpoint says exactly why ("this name points
 *                       at another node"). That is the fact I inferred from placement data and got
 *                       wrong; it was one GET away the whole time.
 *   /caddy/upstreams  — every backend with its request count and FAIL count. The answer to "is the
 *                       app backend actually up", which nothing displayed.
 *   /caddy/storage    — where certificates live and whether that path is genuinely shared. The
 *                       gluster question that made "certificates sync across the cluster" look
 *                       true on a one-node cluster for weeks.
 *   /caddy/live       — process, connections, memory, and RESTARTS: a crash-looping Caddy reads
 *                       "active" at every instant it is looked at, and only the restart count says
 *                       otherwise.
 *
 * The served-by header is here too. Every node's Caddy stamps x-served-by with its own identity —
 * it works, it is the only trustworthy answer to "which node served this response", and no screen
 * mentioned it.
 */

type Live = {
  connections?: { established?: number; ports?: string; time_wait?: number };
  resources?: {
    running?: boolean;
    active_state?: string;
    uptime_s?: number;
    restarts?: number;
    cpu_percent?: number;
    mem_bytes?: number;
    fds?: number;
    fd_max?: number;
    unread?: string[];
  } | null;
  resources_unread?: string;
};
type Upstream = { address: string; num_requests: number; fails: number };
type TlsItem = {
  domain: string;
  tls_mode?: string;
  effective_tls?: string;
  tls_pending?: boolean;
  resolves_here?: boolean;
  resolved_ips?: string[] | null;
  why_held?: string;
};
type StorageItem = { label: string; value: string };
type Plugin = { key: string; title: string; description: string; repo: string; scope: string; available: boolean | null };

const bytes = (n?: number) => (n == null ? "—" : `${(n / 1024 / 1024).toFixed(0)} MB`);
const dur = (s?: number) => {
  if (s == null) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
};

export function CaddyOps() {
  const { client } = useAuth();
  const q = <T,>(key: string, path: string, ms = 30000) =>
    useQuery({ queryKey: [key], queryFn: () => client.apiFetch<T>(path), refetchInterval: ms });

  const live = q<Live>("caddy-live", "/caddy/live", 15000);
  const ups = q<{ count: number; upstreams: Upstream[] | null }>("caddy-upstreams", "/caddy/upstreams");
  const tls = q<{ held: number; total: number; items: TlsItem[] | null }>("caddy-tls-audit", "/caddy/tls/audit", 60000);
  const store = q<{ items: StorageItem[] | null; shared?: boolean; module?: string }>("caddy-storage", "/caddy/storage", 60000);
  const wel = q<{ node_host?: string }>("caddy-welcome-node", "/caddy/welcome", 60000);
  const plugins = q<{ items: Plugin[] | null }>("caddy-plugins", "/caddy/plugins", 60000);

  const r = live.data?.resources;
  // A figure procstat could not read is zero on the wire and measures nothing: shown as —, never 0.
  const unread = (k: string) => !r || (r.unread ?? []).includes(k);
  const notSampled = live.data?.resources_unread;
  const c = live.data?.connections;

  const upCols: DataListColumn<Upstream>[] = [
    { key: "address", label: "Upstream", render: (u) => <span className="font-mono text-[12px]">{u.address}</span> },
    { key: "num_requests", label: "In flight", sortable: true, value: (u) => u.num_requests },
    {
      key: "fails",
      label: "Fails",
      sortable: true,
      value: (u) => u.fails,
      // A non-zero fail count is the difference between "the site is slow" and "the backend is
      // gone", and it was displayed nowhere.
      render: (u) => (u.fails > 0 ? <StatusPill tone="bad">{u.fails}</StatusPill> : <span className="text-muted">0</span>),
    },
  ];

  const tlsCols: DataListColumn<TlsItem>[] = [
    { key: "domain", label: "Domain", sortable: true, render: (t) => <span className="font-mono text-[12px]">{t.domain}</span> },
    {
      key: "effective_tls",
      label: "Serving",
      render: (t) =>
        t.tls_pending ? (
          <StatusPill tone="warn">{t.effective_tls || "internal"} — held</StatusPill>
        ) : (
          <StatusPill tone="ok">{t.effective_tls || t.tls_mode}</StatusPill>
        ),
    },
    {
      key: "resolves_here",
      label: "Points here",
      render: (t) =>
        t.resolves_here ? (
          <StatusPill tone="ok">yes</StatusPill>
        ) : (
          <span className="text-[11.5px] text-muted">{(t.resolved_ips ?? []).join(", ") || "does not resolve"}</span>
        ),
    },
    {
      key: "why_held",
      label: "Why",
      // The endpoint explains the hold in a sentence. Showing it is the whole point: a held
      // certificate with no reason is indistinguishable from a broken one.
      render: (t) => <span className="text-[11.5px] leading-relaxed">{t.why_held || "—"}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <Section title="Running" hint={notSampled ? "not measured — " + notSampled : r?.running ? "active" : "not running"}>
        <StateView loading={live.isLoading} error={live.error} errorContext="the Caddy process">
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-[12.5px]">
            <Stat label="State" value={r?.active_state ?? "—"} tone={notSampled ? undefined : r?.running ? "ok" : "bad"} />
            <Stat label="Uptime" value={dur(r?.uptime_s)} />
            {/* A crash-looping Caddy reads "active" at every instant it is looked at. Only this
                number reveals it, which is why it is beside the state and not buried. */}
            <Stat
              label="Restarts"
              value={String(r?.restarts ?? 0)}
              tone={(r?.restarts ?? 0) > 0 ? "warn" : undefined}
            />
            <Stat label="Memory" value={unread("memory") ? "—" : bytes(r?.mem_bytes)} />
            <Stat label="CPU" value={!unread("cpu") && r?.cpu_percent != null ? `${r.cpu_percent}%` : "—"} />
            <Stat
              label="File descriptors"
              value={r?.fds != null ? `${r.fds}${r.fd_max ? ` / ${r.fd_max}` : ""}` : "—"}
            />
            <Stat label="Connections" value={`${c?.established ?? 0} on ${c?.ports ?? "—"}`} />
          </div>
        </StateView>
      </Section>

      <ReloadsEveryNode />

      <Section title="Backends" hint={ups.data ? `${ups.data.count} upstream(s)` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <StateView loading={ups.isLoading} error={ups.error} errorContext="the upstreams">
            <DataList
              items={ups.data?.upstreams ?? []}
              columns={upCols}
              getRowKey={(u) => u.address}
              emptyMessage="Caddy is proxying to nothing on this node."
              emptyHint="Static and PHP sites have no upstream; only proxied apps appear here."
            />
          </StateView>
        </div>
      </Section>

      <Section
        title="Certificates"
        hint={tls.data ? `${tls.data.held} held of ${tls.data.total}` : undefined}
        bodyClassName="p-0"
      >
        <div className="p-4">
          <StateView loading={tls.isLoading} error={tls.error} errorContext="the certificate audit">
            <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
              A HELD domain is served a self-signed certificate on purpose: this node will not ask a
              public authority for a name that does not resolve to it, because a failed challenge
              burns the rate limit for that name across the whole cluster. Held is not broken — the
              reason column says which it is.
            </p>
            <DataList
              items={tls.data?.items ?? []}
              columns={tlsCols}
              getRowKey={(t) => t.domain}
              emptyMessage="No vhost has a certificate on this node."
            />
          </StateView>
        </div>
      </Section>

      <Section title="Where certificates are stored">
        <StateView loading={store.isLoading} error={store.error} errorContext="certificate storage">
          <div className="space-y-1.5 text-[12.5px] leading-relaxed">
            {(store.data?.items ?? []).map((i) => (
              <div key={i.label}>
                <span className="text-muted">{i.label}: </span>
                {i.value}
              </div>
            ))}
            {wel.data?.node_host && (
              // The served-by header. It works on every node and nothing displayed it, so the
              // question "which node answered this request" was being inferred from placement
              // data instead of read from the response.
              <div className="mt-2 border-t border-line pt-2">
                <span className="text-muted">Responses from this node carry: </span>
                <span className="font-mono">x-served-by: {wel.data.node_host}</span>
                <div className="mt-0.5 text-[11.5px] text-muted">
                  Every node stamps its own. It is the only trustworthy answer to which node served
                  a request — a status code cannot tell you, and both nodes answer 200.
                </div>
              </div>
            )}
          </div>
        </StateView>
      </Section>

      <RedisStorageMove module={store.data?.module} />

      <UnservedCertificates />

      {/* Which plugins the RUNNING binary carries. "available" is computed live from
          `caddy list-modules`, so this reflects the actual build on this node — the answer to
          "is layer4 / the WAF / brotli present?". vhost-scoped ones gate a site's Configure
          toggles; core ones (Redis cert storage, layer4) are infrastructure. */}
      <Section
        title="Plugins"
        hint={plugins.data ? `${(plugins.data.items ?? []).filter((p) => p.available).length}/${(plugins.data.items ?? []).length} built in` : undefined}
      >
        <StateView loading={plugins.isLoading} error={plugins.error} errorContext="the Caddy plugin catalog">
          <div className="space-y-1.5 text-[12.5px]">
            {(plugins.data?.items ?? []).map((p) => (
              <div key={p.key} className="flex flex-wrap items-baseline gap-2 border-b border-line py-1.5">
                <span className="font-medium">{p.title}</span>
                <StatusPill tone={p.available ? "ok" : "neutral"}>{p.available ? "built in" : p.available === false ? "not built" : "not checked"}</StatusPill>
                <span className="rounded border border-line bg-bg px-1.5 py-0.5 text-[10.5px] uppercase tracking-wide text-muted">{p.scope}</span>
                <span className="w-full text-[11.5px] leading-relaxed text-muted">{p.description}</span>
                <span className="font-mono text-[11px] text-muted">{p.repo}</span>
              </div>
            ))}
          </div>
        </StateView>
      </Section>
    </div>
  );
}

/**
 * Certificates nothing serves (caddy/certforget.go, 2026-10-25). A domain delete forgets its own; this
 * lists what was left behind before that existed — old drill and test sites, an evicted node — and
 * forgets one name at a time. The server decides "served" (a site, a live domain and the hosts it
 * implies, a node's address, a port grant) and REFUSES a served name, so the button cannot take a
 * certificate in use; it says what it removes and that each node's folder follows on its next pass.
 */
function UnservedCertificates() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [forgetting, setForgetting] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const q = useQuery({
    queryKey: ["caddy-certs-unserved"],
    queryFn: () => client.apiFetch<{ names: string[] | null; count: number; note?: string }>("/caddy/certificates/unserved"),
    refetchInterval: 120000,
  });
  const names = q.data?.names ?? [];

  const forget = (name: string) => {
    setBusy(true);
    void client
      .apiFetch<{ forgotten: unknown; records_removed: number; note?: string }>(`/caddy/certificates/${encodeURIComponent(name)}`, {
        method: "DELETE",
      })
      .then((a) => {
        notify({
          kind: "success",
          title: `${name}: certificates forgotten`,
          body: `${a.records_removed} record(s) removed. ${a.note ?? ""}`.trim(),
        });
        void qc.invalidateQueries({ queryKey: ["caddy-certs-unserved"] });
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${name} was not forgotten`, body: e instanceof Error ? e.message : String(e) }))
      .finally(() => {
        setBusy(false);
        setForgetting(null);
      });
  };

  return (
    <Section title="Certificates nothing serves" hint={q.data ? `${q.data.count} name(s)` : undefined}>
      <StateView
        loading={q.isLoading}
        error={q.error}
        errorContext="the unserved certificates"
        empty={!q.isLoading && names.length === 0}
        emptyMessage="Every certificate on record belongs to a name something on this cluster serves."
      >
        <ul className="space-y-1">
          {names.map((n) => (
            <li key={n} className="flex flex-wrap items-center justify-between gap-2 border-b border-line py-1.5">
              <span className="font-mono text-[12.5px]">{n}</span>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setForgetting(n)}>
                Forget
              </Button>
            </li>
          ))}
        </ul>
        {q.data?.note && <p className="mt-2 text-[11.5px] leading-relaxed text-muted">{q.data.note}</p>}
      </StateView>
      <ConfirmDialog
        open={!!forgetting}
        onOpenChange={(o) => !o && setForgetting(null)}
        title={`Forget the certificates for ${forgetting ?? ""}?`}
        destructive
        confirmLabel="Forget"
        pending={busy}
        onConfirm={() => forgetting && forget(forgetting)}
        body={
          <p className="text-[12.5px] leading-relaxed">
            Removes this name's certificate records and Caddy's stored copies under every issuer; each node's
            certificate folder drops it on its next pass. Nothing on the cluster serves the name, so no site is
            affected. If the name is put back on a site later, a new certificate is obtained for it — which counts
            against the issuer's rate limit for that domain.
          </p>
        }
      />
    </Section>
  );
}

// GET /caddy/reload-state — each node's own record (migration 0152), plus each node's newest reloads.
type NodeReload = {
  node: string;
  needs_reload: boolean | null;
  pending_files?: string[] | null;
  loaded_at?: string;
  last_reload_at?: string;
  last_reload_kind?: string;
  last_reload_ok?: boolean | null;
  last_reason?: string;
  restart_pending: boolean;
  detail?: string;
  measured_at: string;
  stale: boolean;
};
type ReloadEvent = { node: string; at: string; kind: string; reason: string; ok: boolean; detail?: string; changed_files?: string[] | null };
type ReloadState = { node: string; nodes: NodeReload[] | null; history: Record<string, ReloadEvent[] | null> | null; note?: string };

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : "—");
const shortPath = (p: string) => p.replace("/etc/caddy/sites-enabled/", "").replace("/etc/caddy/", "");

/**
 * Whether each node's Caddy is serving what is on its disk, and every reload, restart and start the
 * panel did there (Oz, 2026-10-08: "record if caddy needs a reload and when it was last reloaded, per
 * node" — then "add it to the console per node"). Each node writes its own row every gate pass, so
 * this one page shows every node without asking any of them; a row not refreshed for ten minutes is
 * marked stale rather than shown as current.
 */
function ReloadsEveryNode() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["caddy-reload-state"],
    queryFn: () => client.apiFetch<ReloadState>("/caddy/reload-state?history=5"),
    refetchInterval: 30000,
  });
  const nodes = q.data?.nodes ?? [];
  const pending = nodes.filter((n) => n.needs_reload && !n.stale).length;
  const failed = nodes.filter((n) => n.last_reload_ok === false && !n.stale).length;

  const cols: DataListColumn<NodeReload>[] = [
    { key: "node", label: "Node", sortable: true, value: (n) => n.node, render: (n) => <span className="font-mono text-[12px]">{n.node}</span> },
    {
      key: "needs_reload",
      label: "Serving what is on disk",
      render: (n) =>
        n.stale ? (
          <StatusPill tone="neutral">not reporting</StatusPill>
        ) : n.needs_reload == null ? (
          <StatusPill tone="neutral">unknown</StatusPill>
        ) : n.needs_reload ? (
          <div>
            <StatusPill tone="warn">reload needed</StatusPill>
            <div className="mt-1 font-mono text-[11px] text-muted">{(n.pending_files ?? []).map(shortPath).join(", ")}</div>
          </div>
        ) : (
          <StatusPill tone="ok">yes</StatusPill>
        ),
    },
    { key: "loaded_at", label: "Last loaded", value: (n) => n.loaded_at ?? "", render: (n) => <span className="text-[12px]">{when(n.loaded_at)}</span> },
    {
      key: "last_reload_at",
      label: "Panel's last reload",
      value: (n) => n.last_reload_at ?? "",
      render: (n) =>
        n.last_reload_at ? (
          <div className="text-[12px]">
            <StatusPill tone={n.last_reload_ok ? "ok" : "bad"}>
              {n.last_reload_kind} {n.last_reload_ok ? "ok" : "FAILED"}
            </StatusPill>{" "}
            {when(n.last_reload_at)}
            <div className="text-[11.5px] text-muted">{n.last_reason}</div>
          </div>
        ) : (
          <span className="text-[11.5px] text-muted">none recorded</span>
        ),
    },
    {
      key: "restart_pending",
      label: "Restart armed",
      render: (n) => (n.restart_pending ? <StatusPill tone="warn">yes</StatusPill> : <span className="text-muted">—</span>),
    },
    {
      key: "measured_at",
      label: "Measured",
      value: (n) => n.measured_at,
      render: (n) => <span className={`text-[11.5px] ${n.stale ? "text-crit" : "text-muted"}`}>{when(n.measured_at)}</span>,
    },
  ];

  return (
    <Section
      title="Reloads — every node"
      hint={q.data ? (pending || failed ? `${pending} need a reload · ${failed} last reload failed` : `${nodes.length} node(s), all serving what is on disk`) : undefined}
      bodyClassName="p-0"
    >
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="the Caddy reload records">
          <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
            A file written is not a file served until Caddy reloads. Each node says whether its running Caddy has loaded every
            file it reads — naming the ones it has not — and records every reload, restart and start the panel does there, with why.
          </p>
          <DataList items={nodes} columns={cols} getRowKey={(n) => n.node} emptyMessage="No node has recorded its Caddy reloads yet." />
          <div className="mt-4 space-y-3">
            {nodes.map((n) => {
              const events = q.data?.history?.[n.node] ?? [];
              return (
                <div key={n.node}>
                  <div className="mb-1 text-[11px] uppercase tracking-wide text-muted">{n.node} — recent reloads</div>
                  {events.length === 0 ? (
                    <div className="text-[12px] text-muted">None since the record began.</div>
                  ) : (
                    <ul className="space-y-1 text-[12px]">
                      {events.map((e, i) => (
                        <li key={i} className="flex flex-wrap items-baseline gap-2 border-b border-line py-1">
                          <span className="text-muted">{when(e.at)}</span>
                          <StatusPill tone={e.ok ? "ok" : "bad"}>{e.kind}{e.ok ? "" : " failed"}</StatusPill>
                          <span>{e.reason}</span>
                          {e.detail && <span className="text-muted">— {e.detail}</span>}
                          {(e.changed_files ?? []).length > 0 && (
                            <span className="font-mono text-[11px] text-muted">{(e.changed_files ?? []).map(shortPath).join(", ")}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </StateView>
      </div>
    </Section>
  );
}

type OpJob = {
  running?: boolean;
  started?: string;
  finished?: string;
  proven?: boolean;
  error?: string;
  result?: Record<string, unknown> & { files?: number; verified?: number; seconds?: number; import?: { files?: number; verified?: number }; live_storage_here?: string; note?: string };
};

/**
 * Moving Caddy's certificate storage onto Redis (Oz, 2026-10-02): one copy every node reads, no
 * role-based certificate source. Both operations are jobs on this node — the API cancels a request
 * after 60 s and a copy of every certificate takes longer than that across the cluster.
 *   - "Prove the copy" imports the file storage into Redis and reads it back byte for byte; it changes
 *     nothing Caddy uses and can be run any number of times before the switch.
 *   - "Switch to Redis" proves Caddy's account on every Redis server, imports and proves again, records
 *     Redis as the cluster's storage and measures this node's Caddy running on it.
 */
function RedisStorageMove({ module }: { module?: string }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [confirm, setConfirm] = React.useState<"import" | "switch" | null>(null);
  const job = (key: string, path: string) =>
    useQuery({
      queryKey: [key],
      queryFn: () => client.apiFetch<OpJob>(path),
      refetchInterval: (q) => ((q.state.data as OpJob | undefined)?.running ? 3000 : 30000),
    });
  const imp = job("caddy-storage-import", "/caddy/storage/redis/import");
  const sw = job("caddy-storage-switch", "/caddy/storage/redis/switch");
  const start = useMutation({
    mutationFn: (which: "import" | "switch") =>
      client.apiFetch(`/caddy/storage/redis/${which}`, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: () => {
      notify({ kind: "success", title: "Started — the result and its proof appear below" });
      void qc.invalidateQueries({ queryKey: ["caddy-storage-import"] });
      void qc.invalidateQueries({ queryKey: ["caddy-storage-switch"] });
      void qc.invalidateQueries({ queryKey: ["caddy-storage"] });
    },
    onError: (e: unknown) => notify({ kind: "error", title: "It did not start", body: e instanceof Error ? e.message : String(e) }),
  });
  const onRedis = module === "redis";
  const line = (label: string, j?: OpJob) => {
    if (!j || (!j.running && !j.finished)) return <div><span className="text-muted">{label}: </span>never run on this node</div>;
    if (j.running) return <div><span className="text-muted">{label}: </span><StatusPill tone="warn">running since {j.started}</StatusPill></div>;
    const r = j.result ?? {};
    const files = r.import?.files ?? r.files;
    const verified = r.import?.verified ?? r.verified;
    return (
      <div>
        <span className="text-muted">{label}: </span>
        <StatusPill tone={j.proven ? "ok" : "bad"}>{j.proven ? "proven" : "failed"}</StatusPill>{" "}
        <span className="text-[11.5px] text-muted">
          {j.finished}
          {files != null ? ` · ${verified ?? 0}/${files} files read back identical` : ""}
          {r.live_storage_here ? ` · this node's Caddy runs on ${r.live_storage_here}` : ""}
        </span>
        {j.error && <div className="mt-0.5 text-[11.5px] text-crit">{j.error}</div>}
        {r.note && <div className="mt-0.5 text-[11.5px] text-muted">{r.note}</div>}
      </div>
    );
  };
  return (
    <Section title="Move certificate storage to Redis" hint={onRedis ? "on Redis" : module ? `on ${module}` : undefined}>
      <div className="space-y-1.5 text-[12.5px] leading-relaxed">
        <p className="max-w-3xl text-muted">
          On Redis every node reads one copy of every certificate, and none of them switches certificate
          source when the primary moves. The switch first copies the file storage into Redis and proves
          the copy, so Caddy asks the CA for nothing it already has.
        </p>
        {line("Last copy proof", imp.data)}
        {line("Last switch", sw.data)}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button size="sm" disabled={onRedis || start.isPending || imp.data?.running} onClick={() => setConfirm("import")}>
            Prove the copy
          </Button>
          <Button size="sm" variant="danger" disabled={onRedis || start.isPending || sw.data?.running} onClick={() => setConfirm("switch")}>
            Switch to Redis
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm === "switch" ? "Switch every node's Caddy onto Redis?" : "Copy the file storage into Redis and prove it?"}
        confirmLabel={confirm === "switch" ? "Switch" : "Copy"}
        destructive={confirm === "switch"}
        pending={start.isPending}
        body={
          confirm === "switch" ? (
            <div className="space-y-2 text-[12.5px]">
              <p>
                Caddy's account is proven on every Redis server, the storage is copied and read back,
                and only then is Redis recorded as the cluster's storage. This node switches at once;
                every other node within a minute, and its sites within a TLS pass.
              </p>
              <p className="text-muted">
                Afterwards the copy is refused: Redis is the newer storage, and a copy would put older
                certificates over renewed ones.
              </p>
            </div>
          ) : (
            <p className="text-[12.5px]">
              Reads every file of Caddy's storage into Redis and reads Redis back to compare. Caddy keeps
              running on its files; nothing it uses changes.
            </p>
          )
        }
        onConfirm={() => {
          if (confirm) start.mutate(confirm);
          setConfirm(null);
        }}
      />
    </Section>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "ok" | "bad" | "warn" }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted">{label}</div>
      {tone ? <StatusPill tone={tone}>{value}</StatusPill> : <div className="font-mono text-[13px]">{value}</div>}
    </div>
  );
}
